import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import TextButton from 'LuxKit/Button/TextButton'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import LuxPlusLandingBenefitCard from './LuxPlusLandingBenefitCard'
import LuxPlusDestinationCarousel from './LuxPlusDestinationCarousel'
import LuxPlusLandingMembershipSection from './LuxPlusLandingMembershipSection'
import LuxPlusLandingPartnerOffersSection from './LuxPlusLandingPartnerOffersSection'
import LuxPlusTestimonialCarousel from './LuxPlusTestimonialCarousel'
import LuxPlusLandingOfferCarousels from './LuxPlusLandingOfferCarousels'
import LuxPlusLandingFaqSection from './LuxPlusLandingFaqSection'
import LuxPlusLandingBottomCTA from './LuxPlusLandingBottomCTA'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import useShouldShowMembershipCta from 'luxPlus/hooks/useShouldShowMembershipCta'
import { getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LuxPlusPriceStack from '../LuxPlusPriceStack'
import Pill from 'LuxKit/Pill'
import LineLockOpenAltIcon from 'LuxKit/Icons/line/LineLockOpenAltIcon'
import LeLogoIcon from 'LuxKit/Icons/custom/LeLogoIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Divider from 'LuxKit/Divider'
import Image from 'components/Common/Image'
import LineFestivalsIcon from 'LuxKit/Icons/line/LineFestivalsIcon'

const HeroImageWrapper = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
  width: 100%;
`

const HeroImage = styled(Image)`
  width: 100%;
  height: ${rem(220)};
  object-fit: cover;
  ${mediaQueryUp.tablet} {
    height: ${rem(380)};
  }

  ${mediaQueryUp.desktop} {
    img {
      transform: scaleX(-1);
    }
  }
`

const HeroOverlayCard = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: column;
    gap: ${rem(8)};
    position: absolute;
    top: ${rem(60)};
    left: ${rem(24)};
    background-color: ${props => props.theme.palette.neutral.default.eight};
    border-radius: ${props => props.theme.borderRadius.M};
    padding: ${rem(20)};
    max-width: ${rem(280)};
  }
`

const SavePill = styled.div`
  position: absolute;
  top: ${rem(-15)};
  right: ${rem(-6)};
`

const PriceOverlay = styled.div`
  position: absolute;
  top: ${rem(24)};
  right: ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  background-color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(${rem(8)});
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(16)} ${rem(12)};
  box-shadow:
    0 0 ${rem(17)} 0 #00000008,
    0 0 ${rem(5)} 0 #0000000A,
    0 0 ${rem(2)} 0 #0000000A,
    0 0 ${rem(1)} 0 #0000000F;
  ${mediaQueryUp.desktop} {
    top: ${rem(150)};
    left: ${rem(280)};
    right: unset;
  }

`

const MobileTextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const BenefitsGrid = styled.div`
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SpecialOfferText = styled(CaptionTextBlock)`
  color: ${props => props.theme.palette.highlight.secondary.normalForeground};
`

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: center;
  }
`

const OverlayTag = styled.span`
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  background-color: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(${rem(4)});
  border-radius: ${props => props.theme.borderRadius.round};
  padding: ${rem(6)} ${rem(12)};
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${props => props.theme.palette.neutral.default.one};
`

const OverlayCard = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  white-space: nowrap;
  border-radius: ${props => props.theme.borderRadius.M};
  border: 1px solid rgba(255, 255, 255, 0.10);
  background:
    linear-gradient(0deg, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0.30) 100%),
    rgba(255, 255, 255, 0.60);
  backdrop-filter: blur(${rem(4)});
  padding: ${rem(16)} ${rem(20)};
  box-shadow: ${props => props.theme.shadow.flat.small};
`

const SupportOverlay = styled.div`
  position: absolute;
  bottom: ${rem(16)};
  right: ${rem(16)};
`

const ChatBubble = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(${rem(8)});
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(8)} ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 600;
  color: ${props => props.theme.palette.neutral.default.one};
  box-shadow:
    0 0 ${rem(17)} 0 #00000008,
    0 0 ${rem(5)} 0 #0000000A,
    0 0 ${rem(2)} 0 #0000000A,
    0 0 ${rem(1)} 0 #0000000F;
`

const LogoCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.one};
  color: ${props => props.theme.palette.neutral.default.eight};
`

function LuxPlusLandingAboutSection() {
  const intl = useIntl()
  const { handleBecomeMember, handleStartFreePreview } = useLuxPlusLandingActions()
  const showCta = useShouldShowMembershipCta()
  const isPreview = useAppSelector(getIsFreePreviewLuxPlusMember)
  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)
  const offer = useMemo(
    () => subscriptionProduct.offers?.find(o => !o.joinFeeWaived),
    [subscriptionProduct.offers],
  )

  const breakfastTag = intl.formatMessage({
    id: 'kCl3XmfuUI0A7_6o155r',
    defaultMessage: '+ Breakfast',
    description: 'Overlay tag on benefit card showing bonus breakfast inclusion',
  })
  const cocktailsTag = intl.formatMessage({
    id: 'xrwJv_IBBhsPK_3ThYrh',
    defaultMessage: '+ Cocktails',
    description: 'Overlay tag on benefit card showing bonus cocktails inclusion',
  })
  const memberOnlyAccessLabel = intl.formatMessage({
    id: 'g53lEXV911Lz5cVl4MIe',
    defaultMessage: 'Member only access',
    description: 'Overlay label on benefit card showing exclusive member access',
  })
  const chatBubbleText = intl.formatMessage({
    id: 'MmMW8e8jWtCdSdXgYwRX',
    defaultMessage: 'Hi Taylor, how can I help?',
    description: 'Example chat bubble text on concierge benefit card',
  })

  const benefits = useMemo(() => [
    {
      imageSrc: 'cocktails-and-breakfast-inclusions',
      imageAlt: intl.formatMessage({
        id: 'qkBJdLpvluhmUgFXd5JL',
        defaultMessage: 'Cocktails and breakfast inclusions',
        description: 'Alt text for bonus inclusions benefit card image',
      }),
      title: intl.formatMessage({
        id: 'hmW8RS8mBwA1YYZkZCAX',
        defaultMessage: 'Bonus inclusions on stays and sailings',
        description: 'Title for bonus inclusions benefit card',
      }),
      description: intl.formatMessage({
        id: '_CTjCyZvco1KPji5K25f',
        defaultMessage: 'Enjoy bonus hotel credit, daily breakfast, onboard spend and other extras.',
        description: 'Description for bonus inclusions benefit card',
      }),
      imageOverlay:
        <CSSBreakpoint min="tablet">
          <OverlayTag style={{ top: rem(12), right: rem(12) }}>{breakfastTag}</OverlayTag>
          <OverlayTag style={{ bottom: rem(12), left: rem(12) }}>{cocktailsTag}</OverlayTag>
        </CSSBreakpoint>,
    },
    {
      imageSrc: 'mctw66akoysla0mvvqk',
      imageAlt: intl.formatMessage({
        id: 'A8HYZdq5Yhh9uj4W7Dp3',
        defaultMessage: 'Woman swimming in infinity pool',
        description: 'Alt text for early access benefit card image',
      }),
      title: intl.formatMessage({
        id: 'jXyu_b4Gq8zQdbyMdQvi',
        defaultMessage: 'Access the best offers before anyone else',
        description: 'Title for early access benefit card',
      }),
      description: intl.formatMessage({
        id: '-jal84tPoFhEFqkw4yKj',
        defaultMessage: 'Be first to book our most popular hotels, with early access and member-only offers.',
        description: 'Description for early access benefit card',
      }),
      imageOverlay: <CSSBreakpoint min="tablet">
        <OverlayCard style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
          <LineLockOpenAltIcon size="M" />
          <BodyText variant="medium" colour="neutral-one" align="center">{memberOnlyAccessLabel}</BodyText>
        </OverlayCard>
      </CSSBreakpoint>,
    },
    {
      imageSrc: '5z0khcncrahy8qkhzor',
      imageAlt: intl.formatMessage({
        id: 'oGmmIV2R_KQ5Dp8mCrhg',
        defaultMessage: 'Fine dining experience',
        description: 'Alt text for everyday savings benefit card image',
      }),
      title: intl.formatMessage({
        id: '6Qp176jSET-srXP5GpG5',
        defaultMessage: 'Everyday savings beyond travel',
        description: 'Title for everyday savings benefit card',
      }),
      description: intl.formatMessage({
        id: '6acBvh4vhKgitsIkTWy2',
        defaultMessage: 'Enjoy exclusive discounts across dining, experiences and lifestyle brands.',
        description: 'Description for everyday savings benefit card',
      }),
    },
    {
      imageSrc: '4ybcsqdbvhb2vrx0zuf',
      imageAlt: intl.formatMessage({
        id: 'P9RQqYUoJt5uczobCRQG',
        defaultMessage: 'Customer support specialist',
        description: 'Alt text for customer support benefit card image',
      }),
      title: intl.formatMessage({
        id: 'UhgFkS5UgBvMCpz0REO7',
        defaultMessage: 'Priority access to 24/7 customer support',
        description: 'Title for customer support benefit card',
      }),
      description: intl.formatMessage({
        id: '5HNs96GC3Pms7y9Ckfsj',
        defaultMessage: 'Dedicated travel specialists available whenever you or your travellers need help.',
        description: 'Description for customer support benefit card',
      }),
      imageOverlay: <CSSBreakpoint min="tablet">
        <SupportOverlay>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <LogoCircle><LeLogoIcon size="XS" /></LogoCircle>
            <ChatBubble>{chatBubbleText}</ChatBubble>
          </Group>
        </SupportOverlay>
      </CSSBreakpoint>,
    },
  ], [intl, breakfastTag, cocktailsTag, memberOnlyAccessLabel, chatBubbleText])

  return (<>
    <LayoutContainer>
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={8} horizontalAlign="center">
          <HeadingTextBlock variant="heading2" align="center">
            {intl.formatMessage({
              id: 'GWRseMVnjp0OQpRZjROy',
              defaultMessage: 'Unlimited benefits all year round',
              description: 'Heading for the LuxPlus about section benefit showcase',
            })}
          </HeadingTextBlock>
          {offer && <BodyTextBlock variant="large" colour="neutral-one" align="center">
            <FormattedMessage
              id="9cmGvEF112Irdim2bhvN"
              defaultMessage="Enjoy member-only benefits for {price} per year, and a one-off joining fee of {joinFee}."
              description="Subheading showing LuxPlus subscription price and joining fee"
              values={{
                price: <FormatCurrency value={offer.price} />,
                joinFee: <FormatCurrency value={offer.joinFee} />,
              }}
            />
          </BodyTextBlock>}
          {offer && <SpecialOfferText variant="medium" align="center" startIcon={<LineFestivalsIcon />}>
            <FormattedMessage
              id="7ZUMSJjcF9tdVIDJ2XIO"
              defaultMessage="<strong>Special offer:</strong> No joining fee when you purchase an eligible {programName} offer (save {joinFee})"
              description="Special offer banner showing joining fee waiver when purchasing an eligible offer"
              values={{
                programName: LUXURY_PLUS.PROGRAM_NAME,
                joinFee: <FormatCurrency value={offer.joinFee} />,
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </SpecialOfferText>}
        </Group>

        <HeroImageWrapper>
          <HeroImage
            id="2sh8mhwtif27qzegy6jl"
            alt={intl.formatMessage({
              id: '-w6dljaF5pfa0xpgQHNO',
              defaultMessage: 'Luxury resort sunset pool',
              description: 'Alt text for the hero image in the about section',
            })}
          />
          <HeroOverlayCard>
            <Heading variant="heading4">
              {intl.formatMessage({
                id: 'ykWf-2yMhxejAoJl1h3r',
                defaultMessage: 'Save hundreds with member-only prices',
                description: 'Heading on hero overlay card promoting member pricing',
              })}
            </Heading>
            <BodyText variant="small" colour="neutral-three">
              {intl.formatMessage({
                id: 'C5qckLB7yezQbeJD1FN9',
                defaultMessage: 'Unlock lower prices on hotels, tours and experiences around the world.',
                description: 'Description on hero overlay card about member pricing benefits',
              })}
            </BodyText>
          </HeroOverlayCard>
          <PriceOverlay>
            <SavePill>
              <Pill kind="primary" variant="success">
                <FormattedMessage
                  id="QGS1ZXjIT-g_PCt8A2Fw"
                  defaultMessage="Save {amount}"
                  description="Pill showing example savings amount on hero image"
                  values={{ amount: <FormatCurrency value={100} format="rounded" /> }}
                />
              </Pill>
            </SavePill>
            <LuxPlusPriceStack
              size="M"
              price={2099}
              memberPrice={1999}
              saleUnit=""
              signDisplay="always"
            />
          </PriceOverlay>
        </HeroImageWrapper>

        <MobileTextSection>
          <Heading variant="heading4">
            {intl.formatMessage({
              id: 'ykWf-2yMhxejAoJl1h3r',
              defaultMessage: 'Save hundreds with member-only prices',
              description: 'Heading on hero overlay card promoting member pricing',
            })}
          </Heading>
          <BodyText variant="small" colour="neutral-three">
            {intl.formatMessage({
              id: 'C5qckLB7yezQbeJD1FN9',
              defaultMessage: 'Unlock lower prices on hotels, tours and experiences around the world.',
              description: 'Description on hero overlay card about member pricing benefits',
            })}
          </BodyText>
        </MobileTextSection>

        <BenefitsGrid>

          {benefits.map((benefit) => (
            <LuxPlusLandingBenefitCard
              key={benefit.title}
              imageSrc={benefit.imageSrc}
              imageAlt={benefit.imageAlt}
              title={benefit.title}
              description={benefit.description}
              imageOverlay={benefit.imageOverlay}
            />
          ))}
        </BenefitsGrid>

        {showCta && <CTASection>
          <TextButton kind="primary" onClick={handleBecomeMember}>
            {intl.formatMessage({
              id: '3-tM77AW0JCHSsgPbV8Y',
              defaultMessage: 'Become a member',
              description: 'CTA button to start LuxPlus membership purchase on about section',
            })}
          </TextButton>
          {!isPreview && (
            <TextButton kind="secondary" onClick={handleStartFreePreview}>
              {intl.formatMessage({
                id: 'TcSWXJo8LD4Q0iQ_77fY',
                defaultMessage: 'Start free preview',
                description: 'CTA button to start free preview on about section',
              })}
            </TextButton>
          )}
        </CTASection>}
        <Divider kind="primary" />
      </Group>
    </LayoutContainer>
    <CSSBreakpoint min="tablet">
      <LuxPlusDestinationCarousel />
    </CSSBreakpoint>
    <LuxPlusLandingMembershipSection />
    <LuxPlusTestimonialCarousel />
    <LayoutContainer>
      <LuxPlusLandingPartnerOffersSection />
    </LayoutContainer>
    <LuxPlusLandingOfferCarousels />
    <LuxPlusLandingFaqSection />
    {showCta && <LuxPlusLandingBottomCTA />}
  </>
  )
}

export default LuxPlusLandingAboutSection
